import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import useSEO from '../hooks/useSEO';
import { isAdminAuthenticated, setAdminToken } from '../utils/adminAuth';

function AdminLogin() {
  useSEO({
    title: 'Admin Login | Nightfall',
    description: 'Admin login page for Nightfall',
    url: 'https://nightfallshop.com/admin'
  });
  
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAdminAuthenticated()) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!password) {
      setError('Please enter a password');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store admin token securely
        setAdminToken(data.token);
        navigate('/admin/dashboard');
      } else {
        // Handle rate limiting
        if (response.status === 429) {
          setError('Too many login attempts. Please try again in 15 minutes.');
        } else {
          setError(data.error || 'Invalid password');
        }
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Admin login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center p-5 relative">
      <button
        onClick={() => navigate('/')}
        className="absolute top-5 left-5 flex items-center gap-2 px-4 py-2 bg-dark-card/50 border border-dark-border rounded-xl text-white/70 hover:text-white hover:bg-dark-card/70 hover:border-accent-blue/40 transition-all duration-200"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span>Back</span>
      </button>
      <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-8 lg:p-12 max-w-md w-full shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Admin Login</h1>
          <p className="text-white/60 text-sm lg:text-base">Enter your password to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-3 bg-dark-bg/50 border border-dark-border rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all"
              placeholder="Enter admin password"
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AdminLogin;

